import React from 'react';
import { motion } from 'framer-motion';
import Footer from './Footer';

const YtSkipHelper = () => {
  return (
    <div className="about-page policy-page skip-helper-page">
      <div className="about-inner">

        <motion.section
          className="about-intro-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="about-main-title">YOUTUBE SKIP HELPER</h1>
          <p className="about-main-subtitle">
            How Panda presses YouTube&apos;s own &ldquo;Skip ad&rdquo; button for people who find it hard to reach.
          </p>
        </motion.section>

        <hr className="section-divider" />

        <motion.section
          className="policy-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="policy-article">
            <p>
              YouTube Skip Helper is a feature of <strong>Panda</strong> (package <code>com.blurr.voice</code>), an Android
              accessibility app from Steepwind Labs. Panda is declared to Google Play as an accessibility tool.
            </p>

            <h2>What it does</h2>
            <p>
              Some YouTube ads can be skipped after a few seconds. When that happens, YouTube shows its own
              &ldquo;Skip ad&rdquo; button. When YouTube shows that button, Panda taps it for you using the Android
              Accessibility Service. That is the same single tap you could make yourself. Panda does not do anything
              that YouTube does not already offer you.
            </p>

            <h2>Who it is for</h2>
            <p>
              The skip button is small, sits in the corner of the video, and only stays on screen for a limited
              time. That makes it hard to use for many people, including people who:
            </p>
            <ul>
              <li>have motor impairments, tremor, or limited dexterity</li>
              <li>use switch access or voice access to control their phone</li>
              <li>have low vision and struggle to find and hit the button in time</li>
            </ul>
            <p>
              Panda users with disabilities asked us for this feature. It gives them the same choice every other
              viewer already has: to skip an ad once YouTube allows it.
            </p>

            <h2>What it does not do</h2>
            <ul>
              <li>It does <strong>not</strong> block, hide, mute, or remove ads.</li>
              <li>It does <strong>not</strong> skip ads that YouTube marks as non-skippable.</li>
              <li>
                It <strong>never</strong> taps before YouTube offers the skip button. The ad plays for as long as
                YouTube requires.
              </li>
              <li>It does <strong>not</strong> change the YouTube app or its network traffic.</li>
              <li>
                It does <strong>not</strong> collect, store, or send your screen content or viewing data off your
                phone. The check runs entirely on your device.
              </li>
            </ul>

            <h2>You stay in control</h2>
            <p>
              YouTube Skip Helper is on by default for Panda users. After Panda skips an ad, a small notice appears at
              the bottom of the screen that says <strong>&ldquo;YouTube ad skipped by Panda&rdquo;</strong>. It has
              three options:
            </p>
            <ul>
              <li><strong>Keep:</strong> leave the feature on.</li>
              <li><strong>Disable:</strong> turn the feature off straight away.</li>
              <li><strong>Know more:</strong> open this page.</li>
            </ul>
            <p>
              You can also turn off Panda&apos;s accessibility service at any time in Android{' '}
              <strong>Settings &rarr; Accessibility</strong>.
            </p>

            <h2>Questions</h2>

            <h3>Is this an ad blocker?</h3>
            <p>
              No. Every ad still plays. Panda only presses the skip button that YouTube itself shows, and only after
              YouTube shows it. Ads that cannot be skipped are never skipped.
            </p>

            <h3>Does Panda see what I watch?</h3>
            <p>
              The check for the skip button runs only on your phone. No screen content or viewing data from this
              feature leaves your device.
            </p>

            <h3>How do I turn it off?</h3>
            <p>
              Tap <strong>Disable</strong> on the &ldquo;YouTube ad skipped by Panda&rdquo; notice. Or turn off
              Panda&apos;s accessibility service in Android <strong>Settings &rarr; Accessibility</strong>.
            </p>

            <h2>Contact</h2>
            <p>
              Questions or feedback:{' '}
              <a href="mailto:support@heypanda.org" className="policy-link">
                support@heypanda.org
              </a>
              . Read our full{' '}
              <a href="/privacy-policy" className="policy-link">
                privacy policy
              </a>
              .
            </p>
          </div>
        </motion.section>
      </div>

      <Footer />

      <style jsx>{`
        .policy-page {
          background: transparent;
        }

        .policy-article {
          max-width: 800px;
          margin: 0 auto;
        }

        .policy-article h2 {
          font-size: 1.5rem;
          color: var(--primary-color);
          margin-top: 40px;
          margin-bottom: 20px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .policy-article h3 {
          font-size: 1.15rem;
          color: var(--text-primary);
          margin-top: 24px;
          margin-bottom: 12px;
        }

        .policy-article p {
          font-size: 1.05rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 16px;
        }

        .policy-article ul {
          margin-bottom: 24px;
          padding-left: 24px;
        }

        .policy-article li {
          font-size: 1.05rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .policy-article strong {
          color: var(--text-primary);
        }

        .policy-article code {
          background: rgba(255, 255, 255, 0.08);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          color: var(--primary-color);
        }

        .skip-helper-page .policy-link {
          color: var(--primary-color);
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .skip-helper-page .policy-link:hover,
        .skip-helper-page .policy-link:focus-visible {
          color: var(--text-primary);
        }

        .skip-helper-page .policy-link:focus-visible {
          outline: 2px solid var(--primary-color);
          outline-offset: 2px;
        }

        @media (max-width: 768px) {
          .policy-article h2 {
            font-size: 1.25rem;
          }
          .policy-article h3 {
            font-size: 1.05rem;
          }
          .policy-article p,
          .policy-article li {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </div>
  );
};

export default YtSkipHelper;
